/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { FC, Fragment, useState } from "react";
import { Grid, makeStyles, createStyles, FormControl, RadioGroup } from "@material-ui/core";
import { GridSize } from "@material-ui/core/Grid";
import { get } from "lodash";
import { ChangeNames, formModelToChangeSet } from "../../clients/FirebaseClient/types";
import { useFormModel } from "../core/FormModelHelpes";
import { LabledTextField } from "../core/LabledTextField";
import { Select, MenuItem } from "../core/Select";
import { Button } from "../core/Button";
import { Colors } from "../../styles";
import { FormControlLabel } from "../core/FormControlLabel";
import { Radio } from "../core/Radio";
import InputLabel from "@material-ui/core/InputLabel";
import { AddressCompletionField } from "../core/AddressCompletionField";

const useStyles = makeStyles(() =>
    createStyles({
        container: {
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "column",
            padding: 0,
            borderRadius: 8,
            marginBottom: 24,
            fontFamily: "innogyWEB",
            fontStretch: "normal",
            fontStyle: "normal",
            letterSpacing: "normal",
            color: Colors.anthracite,
        },
        title: {
            margin: 0,
            fontSize: 37,
            fontWeight: 500,
            height: 43,
            marginBottom: 24,
        },
        subTitle: {
            margin: 0,
            fontSize: 22,
            fontWeight: 500,
            lineHeight: 1.45,
            height: 32,
            marginBottom: 8,
        },
        text: {
            fontSize: 22,
            fontWeight: 300,
            lineHeight: 1.27,
            marginBottom: 24,
        },
        labledTextfieldLabel: {
            marginLeft: 20,
            height: 28,
            fontSize: 22,
            fontWeight: 300,
            fontStretch: "normal",
            fontStyle: "normal",
            fontFamily: "innogyWEB",
            lineHeight: 1.27,
            letterSpacing: "normal",
            color: Colors.anthracite,
        },
        disclaimerContainer: {
            fontFamily: "innogyWEB",
            fontStretch: "normal",
            fontStyle: "normal",
            letterSpacing: "normal",
            color: Colors.anthracite50,
            fontSize: 18,
        },
    }),
);

type PathProxyField = {
    key: string;
    label: string;
    path?: string;
    width: number;
};

type SalutationField = {
    label: string;
    width: number;
};

type FillerField = {
    width: number;
};

type Field = {
    pathProxyField?: PathProxyField;
    salutationField?: SalutationField;
    fillerField?: FillerField;
};

type Fields = {
    [x: string]: Field;
};

type RenderFields = {
    [x: string]: Field;
    natural: Fields;
    legal: Fields;
};

type Salutation = {
    key: string;
    label: string;
    type: "natural" | "legal";
};

const salutations: Salutation[] = [
    { key: "mr", label: "Herr", type: "natural" },
    { key: "ms", label: "Frau", type: "natural" },
    { key: "diverse", label: "Divers", type: "natural" },
    { key: "family", label: "Familie", type: "natural" },
    { key: "spouses", label: "Eheleute", type: "natural" },
    { key: "company", label: "Firma", type: "legal" },
    { key: "flatSharingCommunity", label: "Wohngemeinschaft", type: "legal" },
    { key: "buildingOwnerAssociation", label: "Bauherrengemeinschaft", type: "legal" },
    { key: "heirCommunity", label: "Erbengemeinschaft", type: "legal" },
    { key: "publicOfficials", label: "Träger öffentlicher Belange", type: "legal" },
];

export const ApplicantForm: FC = () => {
    const classes = useStyles();

    const { getFieldProps, getFieldValue, setFieldValue, values, setValues } =
        useFormModel<ChangeNames.APPLICANT>();
    const [isInitialized, setIsInitialized] = useState(false);

    const naturalFields: Fields = {
        vorname: {
            pathProxyField: {
                key: "Vorname",
                label: "Vorname *",
                path: "applicant.value.partner.natural.firstName",
                width: 6,
            },
        },
        nachname: {
            pathProxyField: {
                key: "Nachname",
                label: "Nachname *",
                path: "applicant.value.partner.natural.lastName",
                width: 6,
            },
        },
        plz: {
            pathProxyField: {
                key: "PLZ",
                label: "PLZ *",
                path: "applicant.value.partner.natural.address.zipCode",
                width: 6,
            },
        },
        ort: {
            pathProxyField: {
                key: "Ort",
                label: "Ort *",
                path: "applicant.value.partner.natural.address.city",
                width: 6,
            },
        },
        strasse: {
            pathProxyField: {
                key: "Strasse",
                label: "Straße *",
                path: "applicant.value.partner.natural.address.street",
                width: 6,
            },
        },
        hausnummer: {
            pathProxyField: {
                key: "Hausnummer",
                label: "Hausnummer *",
                path: "applicant.value.partner.natural.address.houseNumber",
                width: 6,
            },
        },
        email: {
            pathProxyField: {
                key: "Email",
                label: "E-Mail-Adresse",
                path: "applicant.value.partner.natural.email",
                width: 6,
            },
        },
        telefon: {
            pathProxyField: {
                key: "Telefon",
                label: "Telefonnummer",
                path: "applicant.value.partner.natural.phoneNumber",
                width: 6,
            },
        },
    };

    const legalFields: Fields = {
        name: {
            pathProxyField: {
                key: "Name",
                label: "Name *",
                path: "applicant.value.partner.legal.name",
                width: 12,
            },
        },
        plz: {
            pathProxyField: {
                key: "PLZ",
                label: "PLZ *",
                path: "applicant.value.partner.legal.address.zipCode",
                width: 6,
            },
        },
        ort: {
            pathProxyField: {
                key: "Ort",
                label: "Ort *",
                path: "applicant.value.partner.legal.address.city",
                width: 6,
            },
        },
        strasse: {
            pathProxyField: {
                key: "Strasse",
                label: "Straße *",
                path: "applicant.value.partner.legal.address.street",
                width: 6,
            },
        },
        hausnummer: {
            pathProxyField: {
                key: "Hausnummer",
                label: "Hausnummer *",
                path: "applicant.value.partner.legal.address.houseNumber",
                width: 6,
            },
        },
        email: {
            pathProxyField: {
                key: "Email",
                label: "E-Mail-Adresse*",
                path: "applicant.value.partner.legal.email",
                width: 6,
            },
        },
        telefon: {
            pathProxyField: {
                key: "Telefon",
                label: "Telefonnummer *",
                path: "applicant.value.partner.legal.phoneNumber",
                width: 6,
            },
        },
    };

    const fields: RenderFields = {
        salutation: {
            salutationField: {
                label: "Anrede *",
                width: 6,
            },
        },
        salutationFiller: {
            fillerField: { width: 6 },
        },
        natural: naturalFields,
        legal: legalFields,
    };

    const getSalutationFromChangeSet = (): string => {
        const changeSet = formModelToChangeSet(values);
        const applicant = changeSet.applicant;

        if (!applicant) return "";

        const salutation = (
            Object.keys(applicant.partner.salutation) as Array<
                keyof typeof applicant.partner.salutation
            >
        ).find((k) => applicant.partner.salutation[k] === true);

        return salutation ? salutation : "";
    };

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const isNaturalBySalutationValue = (salutationValue: string) =>
        salutations.find((salutation) => salutation.key === salutationValue)?.type === "natural";

    const [isNatural, setIsNatural] = useState(
        isNaturalBySalutationValue(getSalutationFromChangeSet()),
    );

    /* eslint-disable @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any */
    const setSalutation = (newSalutation: string) => {
        const newIsNatural = newSalutation === "" || isNaturalBySalutationValue(newSalutation);

        const legalPath = "applicant.value.partner.legal";
        const legalSubtree = get(values, legalPath);

        const naturalPath = "applicant.value.partner.natural";
        const naturalSubtree = get(values, naturalPath);

        setValues((values) => {
            // Salutation Booleans auf das neue Pattern setzen. (true der Ausgewählte, alle anderen false)
            values.applicant.value.partner.salutation.mr = newSalutation === "mr";
            values.applicant.value.partner.salutation.ms = newSalutation === "ms";
            values.applicant.value.partner.salutation.diverse = newSalutation === "diverse";
            values.applicant.value.partner.salutation.family = newSalutation === "family";
            values.applicant.value.partner.salutation.spouses = newSalutation === "spouses";
            values.applicant.value.partner.salutation.company = newSalutation === "company";
            values.applicant.value.partner.salutation.flatSharingCommunity =
                newSalutation === "flatSharingCommunity";
            values.applicant.value.partner.salutation.buildingOwnerAssociation =
                newSalutation === "buildingOwnerAssociation";
            values.applicant.value.partner.salutation.heirCommunity =
                newSalutation === "heirCommunity";
            values.applicant.value.partner.salutation.publicOfficials =
                newSalutation === "publicOfficials";

            // Hat sich die Gruppe, also legal oder natural geändert?
            if (newIsNatural !== isNatural) {
                // Wenn vorher legal und jetzt natural, dann müssen wir für natural einen Kontainer anlegen
                if (newIsNatural && legalSubtree) {
                    if (!values.applicant.value.partner.natural) {
                        values.applicant.value.partner.natural = {
                            address: {
                                city: { value: "" },
                                houseNumber: { value: "" },
                                street: { value: "" },
                                zipCode: { value: "" },
                            },
                            email: { value: "" },
                            firstName: { value: "" },
                            lastName: { value: "" },
                            phoneNumber: { value: "" },
                        };
                    }
                    values.applicant.value.partner.natural.lastName = {
                        value: get(values, legalPath + ".name.value", "")!.substring(0, 35),
                    };
                    values.applicant.value.partner.natural.email = {
                        value: get(values, legalPath + ".email.value") || null,
                    };
                    values.applicant.value.partner.natural.phoneNumber = {
                        value: get(values, legalPath + ".phoneNumber.value") || null,
                    };
                    values.applicant.value.partner.natural.address.street = {
                        value: get(values, legalPath + ".address.street.value") || "",
                    };
                    values.applicant.value.partner.natural.address.houseNumber = {
                        value: get(values, legalPath + ".address.houseNumber.value") || "",
                    };
                    values.applicant.value.partner.natural.address.zipCode = {
                        value: get(values, legalPath + ".address.zipCode.value") || "",
                    };
                    values.applicant.value.partner.natural.address.city = {
                        value: get(values, legalPath + ".address.city.value") || "",
                    };
                    delete values.applicant.value.partner.legal;
                }
                if (!newIsNatural && naturalSubtree) {
                    // Wenn vorher natural und jetzt legal, dann müssen wir für legal einen Kontainer anlegen
                    if (!values.applicant.value.partner.legal) {
                        values.applicant.value.partner.legal = {
                            address: {
                                city: { value: "" },
                                houseNumber: { value: "" },
                                street: { value: "" },
                                zipCode: { value: "" },
                            },
                            email: { value: "" },
                            name: { value: "" },
                            phoneNumber: { value: "" },
                        };
                    }
                    values.applicant.value.partner.legal.name = {
                        value: get(values, naturalPath + ".lastName.value") || "",
                    };
                    values.applicant.value.partner.legal.email = {
                        value: get(values, naturalPath + ".email.value") || null,
                    };
                    values.applicant.value.partner.legal.phoneNumber = {
                        value: get(values, naturalPath + ".phoneNumber.value") || null,
                    };
                    values.applicant.value.partner.legal.address.street = {
                        value: get(values, naturalPath + ".address.street.value") || "",
                    };
                    values.applicant.value.partner.legal.address.houseNumber = {
                        value: get(values, naturalPath + ".address.houseNumber.value") || "",
                    };
                    values.applicant.value.partner.legal.address.zipCode = {
                        value: get(values, naturalPath + ".address.zipCode.value") || "",
                    };
                    values.applicant.value.partner.legal.address.city = {
                        value: get(values, naturalPath + ".address.city.value") || "",
                    };
                    delete values.applicant.value.partner.natural;
                }
            }
        });
        setIsNatural(newIsNatural);
    };
    /* eslint-enable @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any */

    /* eslint-disable @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any */
    const onChangeSalutation = (e: any) => {
        setSalutation(e.target.value);
    };
    /* eslint-enable @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any */

    const renderField = (field: Field): JSX.Element => {
        const pathProxyField = field.pathProxyField;
        const fillerField = field.fillerField;
        const salutationField = field.salutationField;

        if (pathProxyField && pathProxyField.path) {
            if (["PLZ", "Ort", "Strasse"].includes(pathProxyField.key)) {
                const complete =
                    pathProxyField.key === "PLZ"
                        ? "zipCode"
                        : pathProxyField.key === "Ort"
                        ? "city"
                        : "street";
                const address = isNatural
                    ? values.applicant.value.partner.natural?.address
                    : values.applicant.value.partner.legal?.address;
                return (
                    <Grid
                        item
                        md={pathProxyField.width as GridSize}
                        sm={12}
                        xs={12}
                        key={pathProxyField.key}
                    >
                        <AddressCompletionField
                            label={pathProxyField.label}
                            zipCode={address?.zipCode.value || ""}
                            city={address?.city.value || ""}
                            street={address?.street?.value}
                            complete={complete}
                            onChange={(newValue) =>
                                setFieldValue(`${pathProxyField.path}.value`, newValue)
                            }
                            error={getFieldValue(`${pathProxyField.path}.error`)}
                        />
                    </Grid>
                );
            } else {
                const maxLength = new Map<string, number>(
                    Object.entries({
                        Vorname: 35,
                        Nachname: 35,
                        Name: 70,
                    }),
                ).get(pathProxyField.key);
                return (
                    <Grid
                        item
                        md={pathProxyField.width as GridSize}
                        sm={12}
                        xs={12}
                        key={pathProxyField.key}
                    >
                        <LabledTextField
                            data-testid={pathProxyField.path}
                            label={pathProxyField.label}
                            style={{ width: "100%", flex: 1 }}
                            margin="normal"
                            variant="outlined"
                            inputProps={{ maxLength }}
                            error={!!getFieldValue(`${pathProxyField.path}.error`)}
                            helperText={getFieldValue(`${pathProxyField.path}.error`)}
                            {...getFieldProps(`${pathProxyField.path}.value`)}
                        />
                    </Grid>
                );
            }
        }
        if (salutationField) {
            return (
                <Grid item md={6 as GridSize} sm={12} xs={12}>
                    <InputLabel className={classes.labledTextfieldLabel}>Anrede *</InputLabel>
                    <Select
                        style={{ width: "100%", flex: 1 }}
                        value={getSalutationFromChangeSet()}
                        onChange={onChangeSalutation}
                        error={
                            values.applicant.error !== null &&
                            values.applicant.error?.indexOf("salutation") !== -1
                        }
                    >
                        {[{ key: "", label: "Bitte wählen", type: "natural" }]
                            .concat(salutations)
                            .map((salutation) => (
                                <MenuItem value={salutation.key} key={salutation.key}>
                                    {salutation.label}
                                </MenuItem>
                            ))}
                    </Select>
                </Grid>
            );
        }
        if (fillerField) {
            return <Grid item md={fillerField.width as GridSize} sm={12} xs={12} />;
        }

        return <Fragment />;
    };

    const renderFields = (fields: Fields): JSX.Element => {
        return (
            <Fragment>{Object.keys(fields).map((field) => renderField(fields[field]))}</Fragment>
        );
    };

    const copyBuildingAddress = (): void => {
        const street = get(values, "buildingAddress.street.value") || "";
        const houseNumber = get(values, "buildingAddress.houseNumber.value") || "";
        const zipCode = get(values, "buildingAddress.zipCode.value") || "";
        const city = get(values, "buildingAddress.city.value") || "";

        if (isNatural && values.applicant.value.partner.natural) {
            setValues((values) => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.applicant.value.partner.natural!.address.street = {
                    value: street,
                };
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.applicant.value.partner.natural!.address.houseNumber = {
                    value: houseNumber,
                };
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.applicant.value.partner.natural!.address.zipCode = {
                    value: zipCode,
                };
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.applicant.value.partner.natural!.address.city = {
                    value: city,
                };
            });
        } else if (!isNatural && values.applicant.value.partner.legal) {
            setValues((values) => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.applicant.value.partner.legal!.address.street = {
                    value: street,
                };
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.applicant.value.partner.legal!.address.houseNumber = {
                    value: houseNumber,
                };
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.applicant.value.partner.legal!.address.zipCode = {
                    value: zipCode,
                };
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                values.applicant.value.partner.legal!.address.city = {
                    value: city,
                };
            });
        }
    };

    if (!isInitialized) {
        setSalutation(getSalutationFromChangeSet());
        setIsInitialized(true);
    }

    return (
        <Fragment>
            <div className={classes.container}>
                <div className={classes.text}>Weicht der Antragsteller vom Auftraggeber ab?</div>
                <FormControl component="fieldset" style={{ height: 72, display: "flex" }}>
                    <RadioGroup
                        value={values.applicant.value.equalToInvoiceRecipient ? "no" : "yes"}
                        onChange={(e, v) => {
                            setValues((values) => {
                                values.applicant.value.equalToInvoiceRecipient =
                                    v.indexOf("no") >= 0;
                            });
                        }}
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            flex: 1,
                            width: "50%",
                            minWidth: 300,
                        }}
                    >
                        <FormControlLabel value="yes" control={<Radio />} label="Ja" />
                        <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="Nein"
                            style={{ marginLeft: "auto" }}
                        />
                    </RadioGroup>
                </FormControl>
                {!values.applicant.value.equalToInvoiceRecipient && (
                    <div className={classes.container}>
                        <div className={classes.subTitle} style={{ marginBottom: 16 }}>
                            Antragsteller
                        </div>
                        <div className={classes.text}>
                            Hier erfassen Sie die persönlichen Daten des Antragstellers.
                        </div>

                        <Grid container spacing={2}>
                            {renderField(fields.salutation)}
                            <Grid item md={6 as GridSize} sm={12} xs={12}>
                                <Button
                                    mode="secondary"
                                    style={{
                                        marginTop: 34,
                                        width: "100%",
                                    }}
                                    onClick={copyBuildingAddress}
                                >
                                    Bauadresse übernehmen
                                </Button>
                            </Grid>
                            {isNatural ? renderFields(naturalFields) : renderFields(legalFields)}
                        </Grid>
                        <div className={classes.disclaimerContainer}>
                            <p>* Mit Sternchen gekennzeichnete Felder sind Pflichtfelder.</p>
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    );
};
